import React, { useState, useEffect } from 'react';
import { IDSButton, IDSInput, IDSAlert, IDSCard } from '@inera/ids-react';
import {
  getSubmissionComments,
  createSubmissionComment,
  deleteSubmissionComment,
} from '../../services/E2EAPI';
import { useAuth } from '../../contexts/AuthContext';
import CommentAvatar from '../testreports/CommentAvatar';
import { fetchE2EUserProfilesByIds, resolveE2EUserDisplayName } from '../../utils/e2eUserProfiles';
import { formatDateTimeNoSeconds } from '../../utils/dateTime';

const normalizeIdentifier = (value) => String(value ?? '').trim().toLowerCase();

/**
 * E2E comments carry no role field, only the author id. The submission owner is
 * the tester — everyone else commenting on the submission is a reviewer.
 */
const isReviewerComment = (comment, submissionUserId) => {
  // Masking only happens for reviewer authors on the tester API, so the flag is
  // the most reliable signal we have.
  if (comment?.authorMasked) return true;

  const author = normalizeIdentifier(comment?.author);
  const owner = normalizeIdentifier(submissionUserId);
  if (owner && author) return author !== owner;

  return (
    comment?.commentType === 'REVIEWER_COMMENT' ||
    comment?.commentType === 'reviewer' ||
    author.includes('reviewer')
  );
};

/**
 * Comments on a single submitted answer, rendered as message bubbles —
 * same look and feel as the self-declaration review threads
 * (components/testreports/QuestionCommentThread).
 *
 * Pass `comments` to render a preloaded list (no extra request per answer);
 * omit it to let the thread fetch its own comments.
 */
const SubmissionCommentThread = ({ submissionId, submissionUserId, authorName, answerId, onCommentAdded, comments: providedComments, canAdd = true }) => {
  const { roles = [], user } = useAuth();
  const isReviewer = roles.some((role) => role.toLowerCase().includes('reviewer'));
  const usesProvidedComments = Array.isArray(providedComments);

  const [comments, setComments] = useState(usesProvidedComments ? providedComments : []);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [postError, setPostError] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [showField, setShowField] = useState(false);
  const [commentAuthorProfilesById, setCommentAuthorProfilesById] = useState({});

  const currentUserIdentifiers = [
    user?.id,
    user?.username,
    user?.email,
    authorName,
  ]
    .map(normalizeIdentifier)
    .filter(Boolean);

  const isOwnComment = (comment) => {
    const commentAuthor = normalizeIdentifier(comment?.author);
    return !!commentAuthor && currentUserIdentifiers.includes(commentAuthor);
  };

  const notifyCommentChanged = async () => {
    if (!onCommentAdded) {
      return;
    }

    try {
      await onCommentAdded();
    } catch (callbackError) {
      console.error('Failed to refresh parent comments:', callbackError);
    }
  };

  // ── Keep in sync with preloaded comments, or fetch them when not provided ────
  useEffect(() => {
    if (!usesProvidedComments) return;
    setComments(providedComments);
  }, [usesProvidedComments, providedComments]);

  useEffect(() => {
    if (usesProvidedComments) return;
    if (!submissionId) return;

    let cancelled = false;

    const fetchComments = async () => {
      try {
        const data = await getSubmissionComments(submissionId, isReviewer);
        
        if (!cancelled) {
          // Filter by answerId
          let filtered;
          if (answerId) {
            filtered = data.filter(comment => {
              const commentAnswerId = comment.answerId || comment.answer_id;
              return commentAnswerId === answerId;
            });
          } else {
            // Only show submission-level comments (no answerId at all)
            filtered = data.filter(comment => !comment.answerId && !comment.answer_id);
          }
          
          setComments(filtered);
        }
      } catch (err) {
        console.error('Error fetching submission comments:', err);
        if (!cancelled) setFetchError('Kunde inte hämta kommentarer');
      }
    };

    fetchComments();

    return () => {
      cancelled = true;
    };
  }, [usesProvidedComments, submissionId, answerId, isReviewer]);

  // Resolve display names for the comment authors currently shown
  useEffect(() => {
    let cancelled = false;

    // Masked authors already carry a department name rather than a user id.
    const authorIds = comments
      .filter((comment) => !comment?.authorMasked)
      .map((comment) => comment?.author)
      .filter(Boolean);
    if (authorIds.length === 0) return;

    fetchE2EUserProfilesByIds(authorIds)
      .then((profiles) => {
        if (!cancelled) setCommentAuthorProfilesById((prev) => ({ ...prev, ...profiles }));
      })
      .catch((profileErr) => console.error('Failed to resolve comment author profiles:', profileErr));

    return () => {
      cancelled = true;
    };
  }, [comments]);


  
  // ── Post a new comment ───────────────────────────────────────────────────────
  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      setLoading(true);
      setPostError(null);
      setDeleteError(null);

      const result = await createSubmissionComment(
        submissionId,
        answerId,
        newComment,
        authorName,
        isReviewer
      );

      setComments((prev) => [
        ...prev,
        {
          ...result,
          id: result.id || Date.now(),
          answerId: result.answerId ?? answerId,
          commentText: result.commentText || newComment,
          author: result.author || user?.id || authorName,
          authorDisplayName: authorName,
          createdAt: result.createdAt || result.createdDate || new Date().toISOString(),
        },
      ]);

      setNewComment('');
      setShowField(false);
      await notifyCommentChanged();
      
    } catch (err) {
      console.error('Error posting submission comment:', err);
      setPostError('Kunde inte spara kommentar');
    } finally {
      setLoading(false);
    }
  };
  
  async function handleDeleteComment(commentId) {
    try {
      setDeletingCommentId(commentId);
      setDeleteError(null);

      await deleteSubmissionComment(
        submissionId,
        commentId,
        isReviewer
      );

      setComments((prev) =>
        prev.filter((comment) => String(comment.id) !== String(commentId))
      );
      await notifyCommentChanged();
    } catch (err) {
      console.error('Error deleting submission comment:', err);
      setDeleteError('Kunde inte ta bort kommentar');
    } finally {
      setDeletingCommentId(null);
    }
  }

  const formatDate = (dateString) => formatDateTimeNoSeconds(dateString);

  const getCommentAuthorDisplayName = (comment) => {
    if (comment?.authorMasked) return comment.author;
    return resolveE2EUserDisplayName(comment?.author, commentAuthorProfilesById);
  };

  const hasComments = comments.length > 0;

  return (
    <div className="ids-mt-2">
      {fetchError && <IDSAlert type="error"><p>{fetchError}</p></IDSAlert>}

      {hasComments && (
        <>
          <IDSButton
            size="s"
            tertiary
            type="button"
            icon={isExpanded ? 'chevron-down' : 'chevron-right'}
            onClick={() => setIsExpanded((v) => !v)}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Dölj kommentarer' : 'Visa kommentarer'}
          >
            {`Kommentarer (${comments.length})`}
          </IDSButton>

          {isExpanded && (
            <div className="ids-mt-2 ids-mb-2">
              {comments.map((comment, idx) => {
                const reviewerAuthored = isReviewerComment(comment, submissionUserId);
                const canDeleteComment = !!comment.id && isOwnComment(comment);
                const isDeletingComment = deletingCommentId === comment.id;

                return (
                  <div
                    key={comment.id || idx}
                    className="ids-flex ids-mb-4"
                    style={{
                      flexDirection: 'column',
                      alignItems: reviewerAuthored ? 'flex-start' : 'flex-end',
                    }}
                  >
                    <IDSCard className="ids-text-max-width">
                      <CommentAvatar
                        user={reviewerAuthored ? 2 : 1}
                        label={comment.authorDisplayName || getCommentAuthorDisplayName(comment)}
                        subtitle={reviewerAuthored ? 'Granskare' : 'Testare'}
                      />
                      <div style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{comment.commentText}</div>
                    </IDSCard>
                    <div
                      className="ids-small ids-mt-1"
                      style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}
                    >
                      <span>{formatDate(comment.createdAt || comment.createdDate)}</span>
                      {canDeleteComment && (
                        <button
                          type="button"
                          onClick={() => handleDeleteComment(comment.id)}
                          disabled={isDeletingComment}
                          title="Ta bort kommentar"
                          aria-label="Ta bort kommentar"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ids-palette-error-40, #c73c30)', padding: 0, font: 'inherit' }}
                        >
                          {isDeletingComment ? 'Tar bort...' : 'Ta bort'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {deleteError && (
                <div className="ids-mt-2">
                  <IDSAlert type="error"><p>{deleteError}</p></IDSAlert>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {canAdd && (
        !showField ? (
          <IDSButton size="s" secondary type="button" onClick={() => setShowField(true)}>
            + Kommentar
          </IDSButton>
        ) : (
          <div style={{ maxWidth: 420 }}>
            <IDSInput
              label="Lägg till kommentar"
              type="text"
              maxLength={400}
              placeholder="Skriv en kommentar..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddComment();
              }}
            />
            <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <IDSButton size="s" type="button" onClick={handleAddComment} disabled={loading || !newComment.trim()}>
                Lägg till
              </IDSButton>
              <IDSButton size="s" secondary type="button" onClick={() => { setShowField(false); setPostError(null); }}>
                Avbryt
              </IDSButton>
              {postError && (
                <span style={{ color: 'var(--ids-palette-error-40)', fontSize: '0.85rem' }}>{postError}</span>
              )}
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default SubmissionCommentThread;